import React from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  IconButton, 
  Divider, 
  alpha,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import dayjs from 'dayjs';
import { NoteData } from '../queries';

interface NoteSidebarProps {
  notes: NoteData[];
  selectedNoteId: string | null;
  onSelectNote: (id: string) => void;
  onAddNote: () => void;
  onDeleteNote: (id: string, e: React.MouseEvent) => void;
  onTogglePin: (note: NoteData, e: React.MouseEvent) => void;
}

const NoteSidebar: React.FC<NoteSidebarProps> = ({
  notes,
  selectedNoteId,
  onSelectNote,
  onAddNote,
  onDeleteNote,
  onTogglePin
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      width: { xs: '100%', md: 280 }, 
      display: 'flex', 
      flexDirection: 'column', 
      borderRight: { xs: 'none', md: `1px solid ${theme.palette.divider}` },
      bgcolor: alpha(theme.palette.background.default, 0.5)
    }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
          Notes
        </Typography>
        <IconButton size="small" color="primary" onClick={onAddNote}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
        {notes.map((note) => (
          <ListItem key={note.id} disablePadding>
            <ListItemButton 
              selected={selectedNoteId === note.id}
              onClick={() => onSelectNote(note.id)}
              sx={{ 
                px: 2,
                py: 1.5,
                borderLeft: note.isPinned ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                }
              }}
            >
              <ListItemText 
                primary={note.title || 'Untitled'} 
                secondary={dayjs(isNaN(Number(note.updatedAt)) ? note.updatedAt : Number(note.updatedAt)).format('D MMM YYYY')}
                primaryTypographyProps={{ 
                  variant: 'body2', 
                  fontWeight: selectedNoteId === note.id ? 'bold' : 'medium',
                  noWrap: true 
                }}
                secondaryTypographyProps={{ 
                  variant: 'caption', 
                  noWrap: true,
                  sx: { opacity: 0.6 }
                }}
              />
              <Box className="note-actions" sx={{ display: 'flex', opacity: selectedNoteId === note.id ? 1 : 0 }}>
                <IconButton size="small" onClick={(e) => onTogglePin(note, e)}>
                  {note.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} color="primary" /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                </IconButton>
                <IconButton size="small" onClick={(e) => onDeleteNote(note.id, e)}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default NoteSidebar;
